import { IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';

export const RequestItemContainer = styled('div', {
  shouldForwardProp: (prop) => !['isActive', 'isDragging'].includes(String(prop)),
})<{ isActive?: boolean; isDragging?: boolean }>(({ theme, isActive, isDragging }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  padding: 16,
  gap: 16,
  minHeight: 86,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${isActive ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: 12,
  cursor: 'pointer',
  transition:
    'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, transform 0.15s ease-in-out, opacity 0.15s ease-in-out',
  boxShadow: isActive ? `0 0 0 2px ${theme.palette.primary.lighter}` : 'none',
  // Origin placeholder while this card is being dragged: dashed outline, no fill,
  // faded content. The floating copy (DragOverlay) provides the elevated look.
  ...(isDragging && {
    backgroundColor: 'transparent',
    borderStyle: 'dashed',
    borderColor: theme.palette.divider,
    boxShadow: 'none',
    transform: 'none',
    opacity: 0.55,
  }),

  '&:hover': {
    backgroundColor: theme.palette.grey[120],
    borderColor: isActive ? theme.palette.primary.main : 'transparent',
    '& .request-item-pin': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
}));

export const PinIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'isPinned',
})<{ isPinned?: boolean }>(({ theme, isPinned }) => ({
  padding: 0,
  minWidth: 24,
  minHeight: 24,
  position: 'absolute',
  top: 2,
  right: 2,
  flexShrink: 0,
  opacity: isPinned ? 1 : 0,
  color: isPinned ? theme.palette.text.primary : theme.palette.text.secondary,
  transition: 'opacity 0.15s ease',

  ...(isPinned && {
    '& svg': {
      color: theme.palette.text.secondary,
      fill: theme.palette.text.secondary,
    },
  }),
}));

export const HeaderRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '8px',
});

export const TitleContainer = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  flex: 1,
  minWidth: 0,
});

export const FooterRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  height: 16,
});

export const FooterContent = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

export const IconTextItem = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  color: theme.palette.text.secondary,
  fontSize: 11,
  fontWeight: 500,

  '& svg': {
    flexShrink: 0,
    color: theme.palette.text.secondary,
  },
}));

export const DateText = styled('span', {
  shouldForwardProp: (prop) => prop !== 'colorType',
})<{ colorType?: 'default' | 'warning' | 'error' }>(({ theme, colorType }) => ({
  fontSize: 11,
  fontWeight: 500,
  color:
    colorType === 'error'
      ? theme.palette.error.main
      : colorType === 'warning'
        ? theme.palette.warning.main
        : theme.palette.text.secondary,
}));

export const CompletedCircle = styled('div')(({ theme }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  color: theme.palette.success.main,
  backgroundColor: theme.palette.success.light,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));
